import { useEffect, useState } from 'react';
import api from '@/lib/api';
import { PageHeader, EmptyState } from '@/components/ui-bits';
import { CheckCircle2, Plus, Save, Trash2, X, Sparkles } from 'lucide-react';
import { toast } from 'sonner';

const EMPTY = {
  name: '',
  internal_note: '',
  request_csat: true,
  stage_id: '',
  status: '',
  mark_resolved: true,
  order: 0,
  enabled: true,
};

const STATUSES = [
  { value: '', label: 'Sin cambio' },
  { value: 'open', label: 'Abierto' },
  { value: 'won', label: 'Ganado' },
  { value: 'lost', label: 'Perdido' },
  { value: 'closed', label: 'Cerrado' },
];

export default function CloseMacrosPage() {
  const [items, setItems] = useState([]);
  const [stages, setStages] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get('/whatsapp/inbox/close-macros');
      setItems(data.items || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error al cargar');
    }
  };

  useEffect(() => {
    load();
    api.get('/pipeline/list').then((r) => {
      const pipes = r.data.pipelines || [];
      const all = [];
      pipes.forEach((p) => {
        (p.stages || []).forEach((s) => {
          all.push({ id: s.id, name: `${p.name || 'Pipeline'}: ${s.name}` });
        });
      });
      setStages(all);
    }).catch(() => {
      api.get('/pipeline').then((r) => {
        const p = r.data.pipeline || r.data;
        setStages((p.stages || []).map((s) => ({ id: s.id, name: s.name })));
      }).catch(() => {});
    });
  }, []);

  const save = async () => {
    if (!editing?.name?.trim()) {
      toast.error('Nombre obligatorio');
      return;
    }
    try {
      const payload = {
        name: editing.name.trim(),
        internal_note: editing.internal_note || null,
        request_csat: !!editing.request_csat,
        stage_id: editing.stage_id || null,
        status: editing.status || null,
        mark_resolved: !!editing.mark_resolved,
        order: Number(editing.order) || 0,
        enabled: editing.enabled !== false,
      };
      if (editing.id) await api.patch(`/whatsapp/inbox/close-macros/${editing.id}`, payload);
      else await api.post('/whatsapp/inbox/close-macros', payload);
      toast.success('Guardado');
      setEditing(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error al guardar');
    }
  };

  const del = async (item) => {
    if (!confirm(`¿Eliminar "${item.name}"?`)) return;
    try {
      await api.delete(`/whatsapp/inbox/close-macros/${item.id}`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error');
    }
  };

  const seed = async () => {
    try {
      const { data } = await api.post('/whatsapp/inbox/close-macros/seed');
      toast.success(data.created ? `${data.created} macros creadas` : 'Ya hay macros');
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error');
    }
  };

  return (
    <div>
      <PageHeader
        title="Macros de cierre"
        description="Acciones de un clic en Inbox: nota interna, CSAT, etapa, estado y marcar resuelto."
        actions={(
          <div className="flex gap-2">
            <button type="button" className="btn-ghost" onClick={seed}>
              <Sparkles className="h-4 w-4" /> Ejemplos
            </button>
            <button type="button" className="btn-primary" onClick={() => setEditing({ ...EMPTY })}>
              <Plus className="h-4 w-4" /> Nueva
            </button>
          </div>
        )}
      />

      {items.length === 0 ? (
        <EmptyState
          icon={CheckCircle2}
          title="Sin macros de cierre"
          description="Crea macros o carga los ejemplos para el panel del Inbox."
          cta={<button type="button" className="btn-primary" onClick={seed}>Cargar ejemplos</button>}
        />
      ) : (
        <div className="surface overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-muted-foreground border-b border-border">
                <th className="px-3 py-2">Orden</th>
                <th className="px-3 py-2">Nombre</th>
                <th className="px-3 py-2">Acciones</th>
                <th className="px-3 py-2">Estado</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {items.map((m) => (
                <tr key={m.id} className="border-b border-border/60 hover:bg-accent/50">
                  <td className="px-3 py-2 tabular-nums">{m.order ?? 0}</td>
                  <td className="px-3 py-2 font-medium">{m.name}</td>
                  <td className="px-3 py-2 text-xs text-muted-foreground">
                    {[
                      m.request_csat && 'CSAT',
                      m.internal_note && 'nota',
                      m.status && m.status,
                      m.stage_id && 'etapa',
                      m.mark_resolved && 'resuelto',
                    ].filter(Boolean).join(' · ') || '—'}
                  </td>
                  <td className="px-3 py-2 text-xs">{m.enabled !== false ? 'Activa' : 'Off'}</td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">
                    <button type="button" className="btn-ghost h-7 px-2 text-xs" onClick={() => setEditing({ ...m, stage_id: m.stage_id || '', status: m.status || '' })}>
                      Editar
                    </button>
                    <button type="button" className="btn-ghost h-7 w-7 p-0 text-[hsl(var(--danger))]" onClick={() => del(m)}>
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4" onClick={() => setEditing(null)}>
          <div className="surface w-full max-w-lg p-5" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-display font-semibold">{editing.id ? 'Editar macro' : 'Nueva macro'}</h2>
              <button type="button" className="btn-ghost h-8 w-8 p-0" onClick={() => setEditing(null)}>
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="space-y-3 text-sm">
              <label className="block">Nombre
                <input className="input mt-1" value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} />
              </label>
              <label className="block">Nota interna (opcional)
                <textarea className="input mt-1 min-h-[80px]" value={editing.internal_note || ''} onChange={(e) => setEditing({ ...editing, internal_note: e.target.value })} />
              </label>
              <div className="grid grid-cols-2 gap-2">
                <label className="block text-xs">Estado del lead
                  <select className="input mt-1" value={editing.status || ''} onChange={(e) => setEditing({ ...editing, status: e.target.value })}>
                    {STATUSES.map((s) => <option key={s.value || 'none'} value={s.value}>{s.label}</option>)}
                  </select>
                </label>
                <label className="block text-xs">Orden
                  <input type="number" className="input mt-1" value={editing.order ?? 0} onChange={(e) => setEditing({ ...editing, order: Number(e.target.value) })} />
                </label>
              </div>
              {stages.length > 0 && (
                <label className="block text-xs">Etapa (opcional)
                  <select className="input mt-1" value={editing.stage_id || ''} onChange={(e) => setEditing({ ...editing, stage_id: e.target.value })}>
                    <option value="">Sin cambio</option>
                    {stages.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                  </select>
                </label>
              )}
              <label className="flex items-center gap-2">
                <input type="checkbox" checked={!!editing.request_csat} onChange={(e) => setEditing({ ...editing, request_csat: e.target.checked })} />
                Solicitar CSAT (webchat)
              </label>
              <label className="flex items-center gap-2">
                <input type="checkbox" checked={!!editing.mark_resolved} onChange={(e) => setEditing({ ...editing, mark_resolved: e.target.checked })} />
                Marcar resuelto (sale de la cola activa)
              </label>
              <label className="flex items-center gap-2">
                <input type="checkbox" checked={editing.enabled !== false} onChange={(e) => setEditing({ ...editing, enabled: e.target.checked })} />
                Activa
              </label>
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <button type="button" className="btn-ghost" onClick={() => setEditing(null)}>Cancelar</button>
              <button type="button" className="btn-primary" onClick={save}><Save className="h-4 w-4" /> Guardar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
